import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { adminAPI, adminCountryAPI, emergencyAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Users,
  Car,
  Key,
  Truck,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  Globe,
  AlertTriangle,
  Plus,
  Edit,
  Trash2,
  X,
  Coins,
  CreditCard,
  Wallet,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle2,
  CheckCircle,
  Pause,
  Lightbulb,
  RefreshCw,
  Eye,
  Ban,
  Check,
  History,
  Phone,
  Mail,
  MapPin,
  Shield,
  Zap,
  Share2
} from 'lucide-react';

const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = '#00BFA6' }) => (
  <Card className="card-elevated" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-500 text-xs">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
          {subtitle && <p className="text-slate-400 text-xs mt-1">{subtitle}</p>}
        </div>
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
      {trend && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
          <TrendingUp className="w-3 h-3 text-green-600" />
          <span className="text-green-600 text-xs font-medium">{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const MetricRow = ({ label, value, percentage }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-slate-500 text-sm">{label}</span>
    <div className="flex items-center gap-2">
      <span className="text-slate-800 font-medium">{value}</span>
      {percentage !== undefined && (
        <span className="text-slate-400 text-xs">({percentage}%)</span>
      )}
    </div>
  </div>
);

// KPI Card for Executive Dashboard
const KPICard = ({ icon: Icon, title, value, subtitle, trend, trendValue, color = '#00BFA6', size = 'normal' }) => (
  <Card className={`card-elevated ${size === 'large' ? 'lg:col-span-2' : ''}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${color}15` }}
            >
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <p className="text-slate-500 text-sm">{title}</p>
          </div>
          <p className={`font-bold text-slate-800 ${size === 'large' ? 'text-4xl' : 'text-2xl'}`}>{value}</p>
          {subtitle && <p className="text-slate-400 text-xs mt-1">{subtitle}</p>}
        </div>
        {trend && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
            trend === 'up' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
          }`}>
            {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trendValue}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Insight Card for AI Insights
const InsightCard = ({ insight }) => {
  const iconMap = {
    'trending-up': TrendingUp,
    'trending-down': TrendingDown,
    'activity': Activity,
    'pause': Pause,
    'wallet': Wallet,
    'dollar': DollarSign,
  };
  
  const Icon = iconMap[insight.icon] || Lightbulb;
  
  const colorMap = {
    positive: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
  };
  
  const colors = colorMap[insight.type] || colorMap.info;
  
  return (
    <div className={`p-4 rounded-xl ${colors.bg} border ${colors.border}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${colors.icon} mt-0.5`} />
        <div>
          <p className="text-slate-800 font-medium text-sm">{insight.title}</p>
          <p className="text-slate-600 text-xs mt-1">{insight.message}</p>
        </div>
      </div>
    </div>
  );
};

const EmergencyAlert = ({ alert, onResolve }) => (
  <div className={`p-4 rounded-xl ${alert.status === 'active' ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-5 h-5 ${alert.status === 'active' ? 'text-red-600' : 'text-slate-400'}`} />
        <span className="text-slate-800 font-medium">{alert.user_name}</span>
      </div>
      <span className={`px-2 py-1 rounded text-xs ${alert.status === 'active' ? 'bg-red-600 text-white' : 'bg-green-100 text-green-600'}`}>
        {alert.status === 'active' ? 'ACTIVA' : 'Resuelta'}
      </span>
    </div>
    <p className="text-slate-500 text-sm">Tel: {alert.user_phone}</p>
    <p className="text-slate-500 text-sm">Servicio: {alert.service_type} - {alert.service_id?.slice(0, 8)}</p>
    <p className="text-slate-400 text-xs mt-1">{new Date(alert.created_at).toLocaleString()}</p>
    {alert.status === 'active' && (
      <Button
        onClick={() => onResolve(alert.id)}
        className="mt-2 w-full h-8 bg-green-100 text-green-600 hover:bg-green-200 text-sm"
      >
        Marcar como resuelta
      </Button>
    )}
  </div>
);

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('executive');
  const [stats, setStats] = useState(null);
  const [execDashboard, setExecDashboard] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('GLOBAL'); // GLOBAL or country code
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [emergencyAlerts, setEmergencyAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [execLoading, setExecLoading] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  
  // Security KPIs state
  const [securityKPIs, setSecurityKPIs] = useState(null);
  const [securityKPIsLoading, setSecurityKPIsLoading] = useState(false);
  const [kpiPeriod, setKpiPeriod] = useState(30);
  
  // Emergency management state
  const [selectedEmergency, setSelectedEmergency] = useState(null);
  const [emergencyDetail, setEmergencyDetail] = useState(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencyNote, setEmergencyNote] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolutionType, setResolutionType] = useState('resolved');

  const [countryForm, setCountryForm] = useState({
    code: '',
    name: '',
    currency: '',
    symbol: '',
    language: 'es',
    exchange_rate: 1.0,
    active: true,
    services: { rides: true, rentals: true, deliveries: true }
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Reload executive dashboard when country changes
    if (!loading) {
      loadExecutiveDashboard(selectedCountry);
    }
  }, [selectedCountry]);

  const loadData = async () => {
    try {
      const [statsRes, usersRes, countriesRes, alertsRes, execRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminCountryAPI.getAll().catch(() => ({ data: [] })),
        emergencyAPI.getAlerts().catch(() => ({ data: [] })),
        adminAPI.getExecutiveDashboard(selectedCountry).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setCountries(countriesRes.data);
      setEmergencyAlerts(alertsRes.data);
      setExecDashboard(execRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExecutiveDashboard = async (countryCode) => {
    setExecLoading(true);
    try {
      const res = await adminAPI.getExecutiveDashboard(countryCode);
      setExecDashboard(res.data);
    } catch (error) {
      console.error('Error loading executive dashboard:', error);
    } finally {
      setExecLoading(false);
    }
  };

  const refreshExecutiveDashboard = async () => {
    setExecLoading(true);
    try {
      const res = await adminAPI.getExecutiveDashboard(selectedCountry);
      setExecDashboard(res.data);
      toast.success('Dashboard actualizado');
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setExecLoading(false);
    }
  };

  // Load Security KPIs
  const loadSecurityKPIs = async (countryCode = null, periodDays = 30) => {
    setSecurityKPIsLoading(true);
    try {
      const params = { period_days: periodDays };
      if (countryCode && countryCode !== 'GLOBAL') {
        params.country_code = countryCode;
      }
      const res = await emergencyAPI.getSecurityKPIs(params);
      setSecurityKPIs(res.data);
    } catch (error) {
      console.error('Error loading security KPIs:', error);
      toast.error('Error al cargar KPIs de seguridad');
    } finally {
      setSecurityKPIsLoading(false);
    }
  };

  // Load KPIs when switching to security tab or changing filters
  useEffect(() => {
    if (activeTab === 'security-kpis' && !securityKPIs) {
      loadSecurityKPIs(selectedCountry, kpiPeriod);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'security-kpis') {
      loadSecurityKPIs(selectedCountry, kpiPeriod);
    }
  }, [selectedCountry, kpiPeriod]);

  // Helper to format currency based on current view
  const formatAmount = (amount, showSymbol = true) => {
    const symbol = execDashboard?.view?.symbol || '$';
    const currency = execDashboard?.view?.currency || 'USD';
    if (showSymbol) {
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
    return `${(amount || 0).toFixed(2)} ${currency}`;
  };

  const handleSaveCountry = async () => {
    try {
      if (editingCountry) {
        await adminCountryAPI.update(editingCountry.code, countryForm);
        toast.success('País actualizado');
      } else {
        await adminCountryAPI.create(countryForm);
        toast.success('País creado');
      }
      setShowCountryModal(false);
      setEditingCountry(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteCountry = async (code) => {
    if (!confirm('¿Eliminar este país?')) return;
    try {
      await adminCountryAPI.delete(code);
      toast.success('País eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await emergencyAPI.resolveAlert(alertId);
      toast.success('Alerta resuelta');
      loadData();
    } catch (error) {
      toast.error('Error al resolver alerta');
    }
  };

  // User Management Functions
  const openUserDetail = async (userId) => {
    setUserLoading(true);
    setShowUserModal(true);
    try {
      const res = await adminAPI.getUserDetail(userId);
      setUserDetail(res.data);
      setSelectedUser(res.data.user);
    } catch (error) {
      toast.error('Error al cargar detalles del usuario');
      setShowUserModal(false);
    } finally {
      setUserLoading(false);
    }
  };

  const handleBlockUser = async (userId) => {
    if (!confirm('¿Bloquear este usuario? No podrá acceder a la plataforma.')) return;
    try {
      await adminAPI.blockUser(userId);
      toast.success('Usuario bloqueado');
      loadData();
      if (userDetail) {
        const res = await adminAPI.getUserDetail(userId);
        setUserDetail(res.data);
        setSelectedUser(res.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al bloquear');
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      await adminAPI.unblockUser(userId);
      toast.success('Usuario desbloqueado');
      loadData();
      if (userDetail) {
        const res = await adminAPI.getUserDetail(userId);
        setUserDetail(res.data);
        setSelectedUser(res.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desbloquear');
    }
  };

  // Emergency Management Functions
  const openEmergencyDetail = async (alertId) => {
    setEmergencyLoading(true);
    setShowEmergencyModal(true);
    try {
      const res = await emergencyAPI.getAlertDetail(alertId);
      setEmergencyDetail(res.data);
      setSelectedEmergency(res.data);
    } catch (error) {
      toast.error('Error al cargar detalles de emergencia');
      setShowEmergencyModal(false);
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleAcknowledgeEmergency = async (alertId) => {
    try {
      const res = await emergencyAPI.acknowledgeAlert(alertId);
      toast.success(`Emergencia reconocida. Tiempo de respuesta: ${Math.round(res.data.response_time_seconds)}s`);
      loadData();
      if (emergencyDetail) {
        const detail = await emergencyAPI.getAlertDetail(alertId);
        setEmergencyDetail(detail.data);
        setSelectedEmergency(detail.data);
      }
    } catch (error) {
      toast.error('Error al reconocer emergencia');
    }
  };

  const handleAddEmergencyNote = async (alertId) => {
    if (!emergencyNote.trim()) return;
    try {
      await emergencyAPI.addNote(alertId, emergencyNote);
      toast.success('Nota agregada');
      setEmergencyNote('');
      const detail = await emergencyAPI.getAlertDetail(alertId);
      setEmergencyDetail(detail.data);
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  const handleEscalateEmergency = async (alertId) => {
    if (!confirm('¿Escalar esta emergencia a autoridades externas? Esta acción la marcará como CRÍTICA.')) return;
    try {
      await emergencyAPI.escalateAlert(alertId);
      toast.success('Emergencia escalada a autoridades externas');
      loadData();
      const detail = await emergencyAPI.getAlertDetail(alertId);
      setEmergencyDetail(detail.data);
    } catch (error) {
      toast.error('Error al escalar emergencia');
    }
  };

  const handleResolveEmergency = async (alertId) => {
    if (!resolutionNotes.trim()) {
      toast.error('Ingresa notas de resolución');
      return;
    }
    try {
      const res = await emergencyAPI.resolveAlert(alertId, resolutionNotes, resolutionType);
      toast.success(`Emergencia resuelta. Tiempo total: ${Math.round(res.data.total_response_time_seconds / 60)} minutos`);
      setShowEmergencyModal(false);
      setResolutionNotes('');
      setResolutionType('resolved');
      loadData();
    } catch (error) {
      toast.error('Error al resolver emergencia');
    }
  };

  // Severity color helper
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-600';
      case 'acknowledged': return 'bg-yellow-100 text-yellow-600';
      case 'in_progress': return 'bg-blue-100 text-blue-600';
      case 'escalated': return 'bg-purple-100 text-purple-600';
      case 'resolved': return 'bg-green-100 text-green-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const formatElapsedTime = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
  };

  const openEditCountry = (country) => {
    setEditingCountry(country);
    setCountryForm({
      code: country.code,
      name: country.name,
      currency: country.currency,
      symbol: country.symbol || '$',
      language: country.language || 'es',
      exchange_rate: country.exchange_rate || 1.0,
      active: country.active !== false,
      services: country.services || { rides: true, rentals: true, deliveries: true }
    });
    setShowCountryModal(true);
  };

  const openNewCountry = () => {
    setEditingCountry(null);
    setCountryForm({
      code: '',
      name: '',
      currency: '',
      symbol: '$',
      language: 'es',
      exchange_rate: 1.0,
      active: true,
      services: { rides: true, rentals: true, deliveries: true }
    });
    setShowCountryModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-4 lg:p-8 space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 skeleton rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const activeAlerts = emergencyAlerts.filter(a => a.status === 'active');

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 font-['Outfit']">Panel de Administración</h1>
            <p className="text-slate-500 mt-1">Métricas y gestión de la plataforma</p>
          </div>
          
          {/* Emergency Alert Badge */}
          {activeAlerts.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-xl animate-pulse">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-red-600 font-medium">{activeAlerts.length} Alerta(s) Activa(s)</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 flex-wrap">
            <TabsTrigger value="executive" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">
              <PieChart className="w-4 h-4 mr-1" />
              Ejecutivo
            </TabsTrigger>
            <TabsTrigger value="security-kpis" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">
              <Shield className="w-4 h-4 mr-1" />
              Seguridad
            </TabsTrigger>
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">Vista General</TabsTrigger>
            <TabsTrigger value="countries" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">Países</TabsTrigger>
            <TabsTrigger value="emergencies" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">
              Emergencias {activeAlerts.length > 0 && `(${activeAlerts.length})`}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">Usuarios</TabsTrigger>
          </TabsList>

          {/* Executive Dashboard Tab */}
          <TabsContent value="executive" className="mt-6 space-y-6">
            {/* Header with Country Selector and Refresh */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Dashboard Ejecutivo</h2>
                <p className="text-slate-500 text-sm">
                  KPIs financieros y operativos • 
                  <span className="font-medium ml-1">
                    {execDashboard?.view?.type === 'global' ? 'Vista Global (USD)' : `${execDashboard?.view?.currency || 'USD'}`}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Country Selector */}
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-400" />
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="h-9 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-[#00BFA6] focus:border-transparent"
                    data-testid="country-selector"
                  >
                    <option value="GLOBAL">🌍 Global (USD)</option>
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.code === 'GT' ? '🇬🇹' : c.code === 'MX' ? '🇲🇽' : c.code === 'CO' ? '🇨🇴' : '🌐'} {c.name} ({c.currency})
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshExecutiveDashboard}
                  disabled={execLoading}
                  className="border-slate-200"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${execLoading ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </div>

            {/* Main KPIs Row 1 */}
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              <KPICard
                icon={DollarSign}
                title="Ingresos Hoy"
                value={formatAmount(execDashboard?.revenue?.today)}
                subtitle="GMV del día"
                color="#00BFA6"
              />
              <KPICard
                icon={TrendingUp}
                title="Ingresos Semana"
                value={formatAmount(execDashboard?.revenue?.week)}
                subtitle="Últimos 7 días"
                color="#00BFA6"
              />
              <KPICard
                icon={BarChart3}
                title="Ingresos Mes"
                value={formatAmount(execDashboard?.revenue?.month)}
                subtitle="Últimos 30 días"
                color="#FFD166"
              />
              <KPICard
                icon={PieChart}
                title="Ingresos Año"
                value={formatAmount(execDashboard?.revenue?.year)}
                subtitle="Año en curso"
                trend={execDashboard?.revenue?.yoy_growth > 0 ? 'up' : execDashboard?.revenue?.yoy_growth < 0 ? 'down' : undefined}
                trendValue={execDashboard?.revenue?.yoy_growth ? `${Math.abs(execDashboard?.revenue?.yoy_growth || 0).toFixed(1)}% YoY` : undefined}
                color="#00887A"
              />
              <KPICard
                icon={Coins}
                title="Acumulado Total"
                value={formatAmount(execDashboard?.revenue?.accumulated)}
                subtitle="Histórico"
                color="#00887A"
              />
            </div>

            {/* Country Ranking (Global View Only) */}
            {selectedCountry === 'GLOBAL' && execDashboard?.country_ranking?.length > 0 && (
              <Card className="card-elevated">
                <CardHeader className="pb-2">
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-[#00BFA6]" />
                    Ranking por País
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {execDashboard.country_ranking.slice(0, 4).map((country, idx) => (
                      <div 
                        key={country.code}
                        className={`p-4 rounded-xl ${idx === 0 ? 'bg-[#FFD166]/10 border border-[#FFD166]/30' : 'bg-slate-50'}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">
                            {country.code === 'GT' ? '🇬🇹' : country.code === 'MX' ? '🇲🇽' : country.code === 'CO' ? '🇨🇴' : '🌐'}
                          </span>
                          <span className="text-slate-700 font-medium">{country.name}</span>
                          {idx === 0 && <span className="text-xs bg-[#FFD166] text-white px-1.5 py-0.5 rounded">TOP</span>}
                        </div>
                        <p className="text-xl font-bold text-slate-800">${country.revenue_usd.toFixed(2)}</p>
                        <p className="text-slate-400 text-xs">{country.currency} {country.revenue_local.toFixed(2)} local</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Financial Summary */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Balance Card */}
              <Card className="card-elevated lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-[#00BFA6]" />
                    Resumen Financiero (Mes)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-xl">
                      <p className="text-green-600 text-sm font-medium">Ingresos Brutos</p>
                      <p className="text-2xl font-bold text-green-700">
                        {formatAmount(execDashboard?.revenue?.month)}
                      </p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-xl">
                      <p className="text-red-600 text-sm font-medium">Egresos (Pagos)</p>
                      <p className="text-2xl font-bold text-red-700">
                        {formatAmount(execDashboard?.expenses?.month)}
                      </p>
                      <p className="text-red-400 text-xs mt-1">
                        {((execDashboard?.expenses?.payout_rate || 0) * 100).toFixed(0)}% para conductores/propietarios
                      </p>
                    </div>
                    <div className="p-4 bg-[#00BFA6]/10 rounded-xl">
                      <p className="text-[#00BFA6] text-sm font-medium">Comisión Neta</p>
                      <p className="text-2xl font-bold text-[#00887A]">
                        {formatAmount(execDashboard?.balance?.gross_profit)}
                      </p>
                      <p className="text-[#00BFA6] text-xs mt-1">
                        {((execDashboard?.balance?.commission_rate || 0) * 100).toFixed(0)}% de comisión
                      </p>
                    </div>
                  </div>

                  {/* Revenue by Service with Performance */}
                  <div className="mt-6">
                    <p className="text-slate-600 text-sm font-medium mb-3">Performance por Servicio</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Car className="w-5 h-5 text-[#00BFA6]" />
                          <div>
                            <span className="text-slate-700">Viajes</span>
                            <span className="ml-2 text-xs text-slate-400">
                              ({execDashboard?.services?.performance?.rides?.percentage || 0}% del total)
                            </span>
                          </div>
                        </div>
                        <span className="text-slate-800 font-bold">
                          {formatAmount(execDashboard?.revenue?.by_service?.rides?.month)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Truck className="w-5 h-5 text-[#00887A]" />
                          <div>
                            <span className="text-slate-700">Envíos</span>
                            <span className="ml-2 text-xs text-slate-400">
                              ({execDashboard?.services?.performance?.deliveries?.percentage || 0}% del total)
                            </span>
                          </div>
                        </div>
                        <span className="text-slate-800 font-bold">
                          {formatAmount(execDashboard?.revenue?.by_service?.deliveries?.month)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Key className="w-5 h-5 text-[#FFD166]" />
                          <div>
                            <span className="text-slate-700">Rentas</span>
                            <span className="ml-2 text-xs text-slate-400">
                              ({execDashboard?.services?.performance?.rentals?.percentage || 0}% del total)
                            </span>
                          </div>
                        </div>
                        <span className="text-slate-800 font-bold">
                          {formatAmount(execDashboard?.revenue?.by_service?.rentals?.month)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Accounts & Services */}
              <div className="space-y-4">
                {/* Accounts Receivable */}
                <Card className="card-elevated">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">Por Cobrar</p>
                        <p className="text-xl font-bold text-slate-800">
                          {formatAmount(execDashboard?.accounts?.receivable)}
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-400 text-xs">
                      {execDashboard?.accounts?.pending_services || 0} servicios pendientes de completar
                    </p>
                  </CardContent>
                </Card>

                {/* Active Services */}
                <Card className="card-elevated">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">Servicios Activos</p>
                        <p className="text-xl font-bold text-slate-800">
                          {execDashboard?.services?.active?.total || 0}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <span className="px-2 py-1 bg-[#00BFA6]/10 text-[#00BFA6] rounded">
                        {execDashboard?.services?.active?.rides || 0} viajes
                      </span>
                      <span className="px-2 py-1 bg-[#00887A]/10 text-[#00887A] rounded">
                        {execDashboard?.services?.active?.deliveries || 0} envíos
                      </span>
                      <span className="px-2 py-1 bg-[#FFD166]/20 text-[#B8860B] rounded">
                        {execDashboard?.services?.active?.rentals || 0} rentas
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Completed Today */}
                <Card className="card-elevated">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">Completados Hoy</p>
                        <p className="text-xl font-bold text-slate-800">
                          {execDashboard?.services?.completed_today?.total || 0}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <span className="px-2 py-1 bg-green-100 text-green-600 rounded">
                        {execDashboard?.services?.completed_today?.rides || 0} viajes
                      </span>
                      <span className="px-2 py-1 bg-green-100 text-green-600 rounded">
                        {execDashboard?.services?.completed_today?.deliveries || 0} envíos
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Wallet Metrics & AI Insights */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Wallet Metrics */}
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-[#FFD166]" />
                    Liquidez de Usuarios
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-[#00BFA6]/10 rounded-xl">
                      <p className="text-[#00BFA6] text-sm font-medium">Balance Total Wallets</p>
                      <p className="text-2xl font-bold text-[#00887A]">
                        ${(execDashboard?.wallets?.total_user_balance || 0).toFixed(2)}
                      </p>
                      <p className="text-slate-400 text-xs">USD (normalizado)</p>
                    </div>
                    <div className="p-4 bg-[#FFD166]/10 rounded-xl">
                      <p className="text-[#B8860B] text-sm font-medium">Total DIGGII</p>
                      <p className="text-2xl font-bold text-[#B8860B]">
                        {(execDashboard?.wallets?.total_diggii || 0).toFixed(2)}
                      </p>
                      <p className="text-slate-400 text-xs">Tokens en circulación</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Insights */}
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-[#FFD166]" />
                    Insights Inteligentes (IA-03)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {execDashboard?.insights?.length > 0 ? (
                    <div className="space-y-3">
                      {execDashboard.insights.map((insight, idx) => (
                        <InsightCard key={idx} insight={insight} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400">
                      <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No hay insights disponibles</p>
                      <p className="text-xs mt-1">Los insights se generan con más actividad</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Last Update */}
            {execDashboard?.generated_at && (
              <p className="text-center text-slate-400 text-xs">
                Última actualización: {new Date(execDashboard.generated_at).toLocaleString()}
              </p>
            )}
          </TabsContent>

          {/* Security KPIs Tab */}
          <TabsContent value="security-kpis" className="mt-6 space-y-6">
            {/* Header with filters */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800 font-['Outfit']">
                    KPIs de Seguridad y Operaciones
                  </h2>
                  <p className="text-slate-500 text-sm">
                    Métricas de la Central de Monitoreo Mercapoli Move
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Period Selector */}
                <select
                  value={kpiPeriod}
                  onChange={(e) => setKpiPeriod(Number(e.target.value))}
                  className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                >
                  <option value={7}>Últimos 7 días</option>
                  <option value={30}>Últimos 30 días</option>
                  <option value={90}>Últimos 90 días</option>
                </select>
                
                {/* Country Selector */}
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                >
                  <option value="GLOBAL">🌍 Global</option>
                  {countries.filter(c => c.active).map(c => (
                    <option key={c.code} value={c.code}>
                      {c.code === 'GT' ? '🇬🇹' : '🌐'} {c.name}
                    </option>
                  ))}
                </select>
                
                <Button
                  onClick={() => loadSecurityKPIs(selectedCountry, kpiPeriod)}
                  variant="outline"
                  className="border-teal-200 text-teal-600 hover:bg-teal-50"
                  disabled={securityKPIsLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${securityKPIsLoading ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </div>

            {securityKPIsLoading && !securityKPIs ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
              </div>
            ) : securityKPIs ? (
              <>
                {/* Overview Cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <KPICard
                    icon={AlertTriangle}
                    title="Emergencias Totales"
                    value={securityKPIs.overview?.total_emergencies || 0}
                    subtitle={`${securityKPIs.overview?.today_emergencies || 0} hoy`}
                    color="#EF4444"
                  />
                  <KPICard
                    icon={CheckCircle}
                    title="Tasa de Resolución"
                    value={`${(securityKPIs.overview?.resolution_rate || 0).toFixed(1)}%`}
                    subtitle={`${securityKPIs.overview?.resolved_emergencies || 0} resueltas`}
                    color="#22C55E"
                  />
                  <KPICard
                    icon={Clock}
                    title="Tiempo Promedio Respuesta"
                    value={securityKPIs.response_times?.avg_acknowledgment_minutes ? `${securityKPIs.response_times.avg_acknowledgment_minutes} min` : 'N/A'}
                    subtitle="Hasta primera respuesta"
                    color="#F59E0B"
                  />
                  <KPICard
                    icon={Zap}
                    title="Tiempo Promedio Resolución"
                    value={securityKPIs.response_times?.avg_resolution_minutes ? `${securityKPIs.response_times.avg_resolution_minutes} min` : 'N/A'}
                    subtitle="Hasta cierre del caso"
                    color="#8B5CF6"
                  />
                </div>

                {/* Critical Metric Highlight */}
                {securityKPIs.response_times?.critical_under_8min_rate !== null && (
                  <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-2xl bg-teal-500 flex items-center justify-center shadow-lg shadow-teal-500/30">
                            <Shield className="w-8 h-8 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-teal-800">Emergencias Críticas Resueltas en &lt;8 min</h3>
                            <p className="text-teal-600 text-sm">Indicador clave de operación de la Central</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-5xl font-bold text-teal-600">
                            {securityKPIs.response_times.critical_under_8min_rate || 0}%
                          </p>
                          <p className="text-teal-500 text-sm">Meta: 95%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Charts Row */}
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* By Severity */}
                  <Card className="card-elevated">
                    <CardHeader>
                      <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        Por Severidad
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(securityKPIs.by_severity || {}).map(([severity, count]) => {
                        const colors = {
                          critical: { bg: 'bg-red-500', text: 'text-red-600', label: 'Crítica' },
                          high: { bg: 'bg-orange-500', text: 'text-orange-600', label: 'Alta' },
                          medium: { bg: 'bg-yellow-500', text: 'text-yellow-600', label: 'Media' },
                          low: { bg: 'bg-blue-500', text: 'text-blue-600', label: 'Baja' }
                        };
                        const config = colors[severity] || { bg: 'bg-slate-500', text: 'text-slate-600', label: severity };
                        const total = securityKPIs.overview?.total_emergencies || 1;
                        const percentage = ((count / total) * 100).toFixed(0);
                        
                        return (
                          <div key={severity} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className={`font-medium ${config.text}`}>{config.label}</span>
                              <span className="text-slate-600">{count} ({percentage}%)</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${config.bg} rounded-full transition-all duration-500`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {Object.keys(securityKPIs.by_severity || {}).length === 0 && (
                        <p className="text-slate-400 text-sm text-center py-4">Sin datos</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* By Service Type */}
                  <Card className="card-elevated">
                    <CardHeader>
                      <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                        <Car className="w-4 h-4 text-blue-500" />
                        Por Tipo de Servicio
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(securityKPIs.by_service_type || {}).map(([service, count]) => {
                        const icons = {
                          ride: { icon: Car, color: 'text-blue-500', bg: 'bg-blue-500', label: 'Viajes' },
                          delivery: { icon: Truck, color: 'text-green-500', bg: 'bg-green-500', label: 'Entregas' },
                          rental: { icon: Key, color: 'text-purple-500', bg: 'bg-purple-500', label: 'Alquileres' }
                        };
                        const config = icons[service] || { icon: Activity, color: 'text-slate-500', bg: 'bg-slate-500', label: service };
                        const Icon = config.icon;
                        const total = securityKPIs.overview?.total_emergencies || 1;
                        const percentage = ((count / total) * 100).toFixed(0);
                        
                        return (
                          <div key={service} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${config.bg}/10 flex items-center justify-center`}>
                                <Icon className={`w-5 h-5 ${config.color}`} />
                              </div>
                              <span className="font-medium text-slate-700">{config.label}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-800">{count}</p>
                              <p className="text-slate-400 text-xs">{percentage}%</p>
                            </div>
                          </div>
                        );
                      })}
                      {Object.keys(securityKPIs.by_service_type || {}).length === 0 && (
                        <p className="text-slate-400 text-sm text-center py-4">Sin datos</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Daily Trend */}
                  <Card className="card-elevated">
                    <CardHeader>
                      <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-teal-500" />
                        Tendencia Diaria (7 días)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-end justify-between h-32 gap-2">
                        {(securityKPIs.daily_trend || []).map((day, idx) => {
                          const maxCount = Math.max(...(securityKPIs.daily_trend || []).map(d => d.count), 1);
                          const height = (day.count / maxCount) * 100;
                          
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-xs text-slate-600 font-medium">{day.count}</span>
                              <div 
                                className="w-full bg-teal-500 rounded-t-lg transition-all duration-500"
                                style={{ height: `${Math.max(height, 5)}%` }}
                              />
                              <span className="text-xs text-slate-400">{day.day_name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Operators Performance */}
                {securityKPIs.operators && securityKPIs.operators.length > 0 && (
                  <Card className="card-elevated">
                    <CardHeader>
                      <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-500" />
                        Rendimiento de Operadores
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-100">
                              <th className="text-left py-3 px-4 text-slate-500 text-sm font-medium">Operador</th>
                              <th className="text-center py-3 px-4 text-slate-500 text-sm font-medium">Casos Atendidos</th>
                              <th className="text-center py-3 px-4 text-slate-500 text-sm font-medium">Resueltos</th>
                              <th className="text-center py-3 px-4 text-slate-500 text-sm font-medium">Escalados</th>
                              <th className="text-center py-3 px-4 text-slate-500 text-sm font-medium">Tasa Resolución</th>
                            </tr>
                          </thead>
                          <tbody>
                            {securityKPIs.operators.map((op, idx) => (
                              <tr key={op.operator_id || idx} className="border-b border-slate-50 hover:bg-slate-50">
                                <td className="py-3 px-4">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                      <span className="text-purple-600 font-bold text-sm">
                                        {(op.operator_name || 'O').charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <span className="font-medium text-slate-700">{op.operator_name}</span>
                                  </div>
                                </td>
                                <td className="text-center py-3 px-4 font-bold text-slate-800">{op.cases_handled}</td>
                                <td className="text-center py-3 px-4">
                                  <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full text-sm">{op.resolved}</span>
                                </td>
                                <td className="text-center py-3 px-4">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-sm">{op.escalated}</span>
                                </td>
                                <td className="text-center py-3 px-4">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full rounded-full ${op.resolution_rate >= 80 ? 'bg-green-500' : op.resolution_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                        style={{ width: `${op.resolution_rate}%` }}
                                      />
                                    </div>
                                    <span className="text-sm font-medium text-slate-600">{op.resolution_rate.toFixed(0)}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Trip Sharing Stats */}
                <Card className="card-elevated bg-gradient-to-r from-slate-50 to-blue-50 border-blue-100">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <Share2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800">Viajes Compartidos</h3>
                        <p className="text-slate-500 text-sm">Función de seguridad para terceros de confianza</p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-6">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-blue-600">{securityKPIs.trip_sharing?.active_shared_trips || 0}</p>
                        <p className="text-slate-500 text-sm">Activos ahora</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-slate-800">{securityKPIs.trip_sharing?.total_shared_trips || 0}</p>
                        <p className="text-slate-500 text-sm">Total período</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-teal-600">{securityKPIs.trip_sharing?.total_views || 0}</p>
                        <p className="text-slate-500 text-sm">Visualizaciones</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Last Update */}
                {securityKPIs.generated_at && (
                  <p className="text-center text-slate-400 text-xs">
                    Última actualización: {new Date(securityKPIs.generated_at).toLocaleString()}
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Cargando KPIs de Seguridad...</p>
              </div>
            )}
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* Top Stats */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                title="Usuarios Totales"
                value={stats?.users?.total || 0}
                subtitle={`+${stats?.users?.new_today || 0} hoy`}
                color="#00BFA6"
              />
              <StatCard
                icon={Car}
                title="Viajes Totales"
                value={stats?.operations?.rides?.total || 0}
                subtitle={`${stats?.operations?.rides?.completion_rate || 0}% completados`}
                color="#00BFA6"
              />
              <StatCard
                icon={Truck}
                title="Entregas"
                value={stats?.operations?.deliveries?.total || 0}
                subtitle={`${stats?.operations?.deliveries?.completion_rate || 0}% completadas`}
                color="#00887A"
              />
              <StatCard
                icon={DollarSign}
                title="GMV Total"
                value={`$${(stats?.financial?.gmv?.total || 0).toFixed(2)}`}
                color="#FFD166"
              />
            </div>

            {/* GMV by Service */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#FFD166]" />
                    GMV por Servicio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-[#00BFA6]/10 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Car className="w-5 h-5 text-[#00BFA6]" />
                        <span className="text-slate-700">Viajes</span>
                      </div>
                      <span className="text-slate-800 font-bold">${(stats?.financial?.gmv?.rides || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#FFD166]/10 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Key className="w-5 h-5 text-[#FFD166]" />
                        <span className="text-slate-700">Rentas</span>
                      </div>
                      <span className="text-slate-800 font-bold">${(stats?.financial?.gmv?.rentals || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#00887A]/10 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Truck className="w-5 h-5 text-[#00887A]" />
                        <span className="text-slate-700">Envíos</span>
                      </div>
                      <span className="text-slate-800 font-bold">${(stats?.financial?.gmv?.deliveries || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Methods */}
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#00BFA6]" />
                    Métodos de Pago
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <MetricRow label="Digi Wallet (USD)" value={stats?.financial?.payment_methods?.wallet || 0} />
                    <MetricRow label="DIGGII Tokens" value={stats?.financial?.payment_methods?.diggii || 0} />
                    <MetricRow label="Stripe" value={stats?.financial?.payment_methods?.stripe || 0} />
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">DIGGII Emitidos</span>
                      <span className="text-[#FFD166] font-bold">{(stats?.diggii?.issued || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-slate-500">DIGGII Usados</span>
                      <span className="text-slate-800 font-bold">{(stats?.diggii?.used || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-slate-500">Bonos Referidos</span>
                      <span className="text-green-600 font-bold">{stats?.diggii?.referral_count || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Activity Stats */}
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-slate-800 text-sm">Viajes Hoy/Semana/Mes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">
                    {stats?.operations?.rides?.today || 0} / {stats?.operations?.rides?.week || 0} / {stats?.operations?.rides?.month || 0}
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-slate-800 text-sm">Conductores Activos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{stats?.users?.drivers || 0}</div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-slate-800 text-sm">Dueños de Vehículos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{stats?.users?.vehicle_owners || 0}</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Countries Tab */}
          <TabsContent value="countries" className="mt-6">
            <Card className="card-elevated">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-slate-800 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#00BFA6]" />
                  Gestión de Países y Monedas
                </CardTitle>
                <Button onClick={openNewCountry} className="btn-primary h-10">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar País
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {countries.map((country) => (
                    <div key={country.code} className={`p-4 rounded-xl ${country.active ? 'bg-slate-50' : 'bg-slate-50 opacity-50'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#00BFA6]/15 flex items-center justify-center">
                            <Globe className="w-5 h-5 text-[#00BFA6]" />
                          </div>
                          <div>
                            <p className="text-slate-800 font-medium">{country.name}</p>
                            <p className="text-slate-500 text-sm">
                              {country.code} • {country.currency} ({country.symbol}) • 1 USD = {country.exchange_rate} {country.currency}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs ${country.active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {country.active ? 'Activo' : 'Inactivo'}
                          </span>
                          <Button variant="ghost" size="icon" onClick={() => openEditCountry(country)}>
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteCountry(country.code)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {country.services?.rides && <span className="px-2 py-1 bg-[#00BFA6]/10 text-[#00BFA6] rounded text-xs">Viajes</span>}
                        {country.services?.rentals && <span className="px-2 py-1 bg-[#FFD166]/10 text-[#FFD166] rounded text-xs">Rentas</span>}
                        {country.services?.deliveries && <span className="px-2 py-1 bg-[#00887A]/10 text-[#00887A] rounded text-xs">Envíos</span>}
                      </div>
                    </div>
                  ))}
                  {countries.length === 0 && (
                    <div className="text-center py-8">
                      <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No hay países configurados</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Emergencies Tab */}
          <TabsContent value="emergencies" className="mt-6 space-y-6">
            {/* Emergency Stats Summary */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {emergencyAlerts.filter(a => a.status === 'active').length}
                      </p>
                      <p className="text-slate-500 text-xs">Activas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {emergencyAlerts.filter(a => a.status === 'acknowledged').length}
                      </p>
                      <p className="text-slate-500 text-xs">En atención</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {emergencyAlerts.filter(a => a.status === 'escalated').length}
                      </p>
                      <p className="text-slate-500 text-xs">Escaladas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {emergencyAlerts.filter(a => a.status === 'resolved').length}
                      </p>
                      <p className="text-slate-500 text-xs">Resueltas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Active Emergencies List */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Alertas de Emergencia ({emergencyAlerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {emergencyAlerts.map((alert) => (
                    <div 
                      key={alert.id} 
                      className={`p-4 rounded-xl transition-colors cursor-pointer hover:shadow-md ${
                        alert.status === 'active' ? 'bg-red-50 border border-red-200' : 
                        alert.status === 'escalated' ? 'bg-purple-50 border border-purple-200' :
                        'bg-slate-50'
                      }`}
                      onClick={() => openEmergencyDetail(alert.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            alert.status === 'active' ? 'bg-red-100' : 'bg-slate-100'
                          }`}>
                            <AlertTriangle className={`w-5 h-5 ${
                              alert.status === 'active' ? 'text-red-600 animate-pulse' : 'text-slate-400'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-800 font-medium">{alert.user_name}</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(alert.severity)}`}>
                                {alert.severity?.toUpperCase()}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(alert.status)}`}>
                                {alert.status}
                              </span>
                            </div>
                            <p className="text-slate-500 text-sm mt-1">{alert.initial_message || 'Sin mensaje'}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {alert.user_phone || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Car className="w-3 h-3" /> {alert.service_type}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {formatElapsedTime(alert.elapsed_seconds)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {alert.status === 'active' && (
                            <Button
                              size="sm"
                              className="bg-yellow-500 hover:bg-yellow-600 text-white"
                              onClick={(e) => { e.stopPropagation(); handleAcknowledgeEmergency(alert.id); }}
                            >
                              Reconocer
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openEmergencyDetail(alert.id); }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {emergencyAlerts.length === 0 && (
                    <div className="text-center py-8">
                      <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No hay alertas de emergencia</p>
                      <p className="text-slate-400 text-sm">Las alertas aparecerán aquí cuando los usuarios activen el botón de pánico</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-slate-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#00BFA6]" />
                  Usuarios ({users.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.slice(0, 30).map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.status === 'blocked' ? 'bg-red-100' : 'bg-[#00BFA6]/15'
                        }`}>
                          <Users className={`w-5 h-5 ${user.status === 'blocked' ? 'text-red-500' : 'text-[#00BFA6]'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-slate-800 font-medium">{user.full_name}</p>
                            {user.status === 'blocked' && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">Bloqueado</span>
                            )}
                          </div>
                          <p className="text-slate-500 text-sm">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-600' :
                            user.role === 'driver' ? 'bg-green-100 text-green-600' :
                            user.role === 'vehicle_owner' ? 'bg-yellow-100 text-yellow-600' :
                            'bg-blue-100 text-blue-600'
                          }`}>
                            {user.role}
                          </span>
                          <p className="text-slate-400 text-xs mt-1">{user.country}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-[#00BFA6]"
                            onClick={() => openUserDetail(user.id)}
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {user.role !== 'admin' && (
                            user.status === 'blocked' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-green-500 hover:text-green-600"
                                onClick={() => handleUnblockUser(user.id)}
                                title="Desbloquear"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-400 hover:text-red-600"
                                onClick={() => handleBlockUser(user.id)}
                                title="Bloquear"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* User Detail Modal */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="card-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800">Detalles del Usuario</h3>
                  <Button variant="ghost" size="icon" onClick={() => { setShowUserModal(false); setUserDetail(null); setSelectedUser(null); }}>
                    <X className="w-5 h-5 text-slate-400" />
                  </Button>
                </div>

                {userLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <RefreshCw className="w-8 h-8 text-[#00BFA6] animate-spin" />
                  </div>
                ) : selectedUser && userDetail ? (
                  <div className="space-y-6">
                    {/* User Info */}
                    <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        selectedUser.status === 'blocked' ? 'bg-red-100' : 'bg-[#00BFA6]/15'
                      }`}>
                        <Users className={`w-8 h-8 ${selectedUser.status === 'blocked' ? 'text-red-500' : 'text-[#00BFA6]'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-lg font-bold text-slate-800">{selectedUser.full_name}</h4>
                          <span className={`px-2 py-1 rounded text-xs ${
                            selectedUser.role === 'admin' ? 'bg-purple-100 text-purple-600' :
                            selectedUser.role === 'driver' ? 'bg-green-100 text-green-600' :
                            selectedUser.role === 'vehicle_owner' ? 'bg-yellow-100 text-yellow-600' :
                            'bg-blue-100 text-blue-600'
                          }`}>
                            {selectedUser.role}
                          </span>
                          {selectedUser.status === 'blocked' && (
                            <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">BLOQUEADO</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-slate-500 text-sm">
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selectedUser.email}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selectedUser.phone || 'N/A'}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{selectedUser.country}</span>
                        </div>
                      </div>
                      {selectedUser.role !== 'admin' && (
                        selectedUser.status === 'blocked' ? (
                          <Button
                            onClick={() => handleUnblockUser(selectedUser.id)}
                            className="bg-green-100 text-green-600 hover:bg-green-200"
                          >
                            <Check className="w-4 h-4 mr-2" /> Desbloquear
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={() => handleBlockUser(selectedUser.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Ban className="w-4 h-4 mr-2" /> Bloquear
                          </Button>
                        )
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-[#00BFA6]/10 rounded-xl text-center">
                        <p className="text-2xl font-bold text-[#00887A]">{userDetail.stats?.total_rides || 0}</p>
                        <p className="text-slate-500 text-xs">Viajes ({userDetail.stats?.completed_rides || 0} completados)</p>
                      </div>
                      <div className="p-4 bg-[#00887A]/10 rounded-xl text-center">
                        <p className="text-2xl font-bold text-[#00887A]">{userDetail.stats?.total_deliveries || 0}</p>
                        <p className="text-slate-500 text-xs">Envíos ({userDetail.stats?.completed_deliveries || 0} completados)</p>
                      </div>
                      <div className="p-4 bg-[#FFD166]/10 rounded-xl text-center">
                        <p className="text-2xl font-bold text-[#B8860B]">${userDetail.stats?.total_spent || 0}</p>
                        <p className="text-slate-500 text-xs">Total gastado</p>
                      </div>
                    </div>

                    {/* Wallet Info */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-slate-600 text-sm font-medium mb-2">Wallet</p>
                      <div className="flex gap-4">
                        <div>
                          <p className="text-lg font-bold text-slate-800">${selectedUser.wallet_balance?.toFixed(2) || '0.00'}</p>
                          <p className="text-slate-400 text-xs">Balance USD</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-[#FFD166]">{selectedUser.diggii_balance?.toFixed(2) || '0.00'}</p>
                          <p className="text-slate-400 text-xs">DIGGII Tokens</p>
                        </div>
                      </div>
                    </div>

                    {/* Service History */}
                    <div>
                      <p className="text-slate-600 text-sm font-medium mb-2 flex items-center gap-2">
                        <History className="w-4 h-4" /> Historial de Servicios
                      </p>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {[
                          ...(userDetail.history?.rides || []).map(r => ({...r, type: 'ride'})),
                          ...(userDetail.history?.deliveries || []).map(d => ({...d, type: 'delivery'}))
                        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              {item.type === 'ride' ? <Car className="w-4 h-4 text-[#00BFA6]" /> : <Truck className="w-4 h-4 text-[#00887A]" />}
                              <div>
                                <p className="text-slate-700 text-sm">
                                  {item.type === 'ride' ? 'Viaje' : 'Envío'} - {item.status}
                                </p>
                                <p className="text-slate-400 text-xs">
                                  {new Date(item.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <span className="text-slate-800 font-medium">
                              ${(item.final_price || item.estimated_price || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        {(!userDetail.history?.rides?.length && !userDetail.history?.deliveries?.length) && (
                          <p className="text-slate-400 text-center py-4">Sin historial de servicios</p>
                        )}
                      </div>
                    </div>

                    {/* Account Info */}
                    <div className="p-4 bg-slate-50 rounded-xl text-xs text-slate-400">
                      <p>ID: {selectedUser.id}</p>
                      <p>Creado: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : 'N/A'}</p>
                      <p>Código Referido: {selectedUser.referral_code || 'N/A'}</p>
                    </div>
                  </div>
                ) : null}
              </div>
            </Card>
          </div>
        )}

        {/* Emergency Detail Modal */}
        {showEmergencyModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="card-elevated w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    <h3 className="text-xl font-bold text-slate-800">Detalle de Emergencia</h3>
                    {emergencyDetail?.severity && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(emergencyDetail.severity)}`}>
                        {emergencyDetail.severity.toUpperCase()}
                      </span>
                    )}
                    {emergencyDetail?.status && (
                      <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(emergencyDetail.status)}`}>
                        {emergencyDetail.status}
                      </span>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => { setShowEmergencyModal(false); setEmergencyDetail(null); }}>
                    <X className="w-5 h-5 text-slate-400" />
                  </Button>
                </div>

                {emergencyLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <RefreshCw className="w-8 h-8 text-red-500 animate-spin" />
                  </div>
                ) : emergencyDetail ? (
                  <div className="space-y-6">
                    {/* User & Driver Info */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-red-50 rounded-xl">
                        <p className="text-red-600 text-sm font-medium mb-2">Usuario en Emergencia</p>
                        <p className="text-slate-800 font-bold">{emergencyDetail.user_name}</p>
                        <div className="mt-2 space-y-1 text-sm text-slate-600">
                          <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {emergencyDetail.user_phone || 'N/A'}</p>
                          <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {emergencyDetail.user_email || 'N/A'}</p>
                          <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {emergencyDetail.country}</p>
                        </div>
                      </div>
                      {emergencyDetail.driver_info && (
                        <div className="p-4 bg-blue-50 rounded-xl">
                          <p className="text-blue-600 text-sm font-medium mb-2">Conductor Asociado</p>
                          <p className="text-slate-800 font-bold">{emergencyDetail.driver_info.name}</p>
                          <div className="mt-2 space-y-1 text-sm text-slate-600">
                            <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {emergencyDetail.driver_info.phone || 'N/A'}</p>
                            <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {emergencyDetail.driver_info.email || 'N/A'}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Service Context */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-slate-600 text-sm font-medium mb-2">Contexto del Servicio</p>
                      <div className="flex items-center gap-2 mb-2">
                        {emergencyDetail.service_type === 'ride' && <Car className="w-5 h-5 text-[#00BFA6]" />}
                        {emergencyDetail.service_type === 'delivery' && <Truck className="w-5 h-5 text-[#00887A]" />}
                        {emergencyDetail.service_type === 'rental' && <Key className="w-5 h-5 text-[#FFD166]" />}
                        <span className="text-slate-800 font-medium capitalize">{emergencyDetail.service_type}</span>
                        <span className="text-slate-400 text-xs">ID: {emergencyDetail.service_id?.slice(0, 8)}</span>
                      </div>
                      {emergencyDetail.service_context && (
                        <div className="text-sm text-slate-600 space-y-1">
                          {emergencyDetail.service_context.pickup && (
                            <p>📍 Origen: {emergencyDetail.service_context.pickup.address || `${emergencyDetail.service_context.pickup.lat}, ${emergencyDetail.service_context.pickup.lng}`}</p>
                          )}
                          {emergencyDetail.service_context.destination && (
                            <p>🎯 Destino: {emergencyDetail.service_context.destination.address || `${emergencyDetail.service_context.destination.lat}, ${emergencyDetail.service_context.destination.lng}`}</p>
                          )}
                          {emergencyDetail.service_context.status && (
                            <p>Estado: {emergencyDetail.service_context.status}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Initial Message */}
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <p className="text-yellow-700 text-sm font-medium mb-1">Mensaje de Emergencia</p>
                      <p className="text-slate-800">{emergencyDetail.initial_message || 'Sin mensaje'}</p>
                    </div>

                    {/* Location */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-slate-600 text-sm font-medium mb-2">Última Ubicación Conocida</p>
                      <p className="text-slate-800">
                        Lat: {emergencyDetail.current_location?.lat?.toFixed(6)}, Lng: {emergencyDetail.current_location?.lng?.toFixed(6)}
                      </p>
                      <p className="text-slate-400 text-xs mt-1">
                        {emergencyDetail.location_history?.length || 0} actualizaciones de ubicación
                      </p>
                    </div>

                    {/* Timeline */}
                    <div>
                      <p className="text-slate-600 text-sm font-medium mb-3 flex items-center gap-2">
                        <History className="w-4 h-4" /> Timeline del Incidente
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {emergencyDetail.timeline?.map((event, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg">
                            <div className={`w-2 h-2 rounded-full mt-2 ${
                              event.event_type === 'created' ? 'bg-red-500' :
                              event.event_type === 'resolved' ? 'bg-green-500' :
                              event.event_type === 'escalated' ? 'bg-purple-500' :
                              'bg-blue-500'
                            }`} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-slate-800 text-sm font-medium">{event.event_type}</span>
                                <span className="text-slate-400 text-xs">
                                  {new Date(event.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-slate-500 text-xs">{event.actor_name}</p>
                              {event.details?.note && (
                                <p className="text-slate-600 text-sm mt-1 italic">"{event.details.note}"</p>
                              )}
                              {event.details?.message && (
                                <p className="text-slate-600 text-sm mt-1">{event.details.message}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    {emergencyDetail.status !== 'resolved' && (
                      <div className="space-y-4 pt-4 border-t border-slate-200">
                        {/* Add Note */}
                        <div>
                          <Label className="text-slate-700 text-sm">Agregar Nota</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              value={emergencyNote}
                              onChange={(e) => setEmergencyNote(e.target.value)}
                              placeholder="Escribir nota..."
                              className="flex-1"
                            />
                            <Button
                              onClick={() => handleAddEmergencyNote(emergencyDetail.id)}
                              disabled={!emergencyNote.trim()}
                              className="bg-blue-500 hover:bg-blue-600"
                            >
                              Agregar
                            </Button>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          {emergencyDetail.status === 'active' && (
                            <Button
                              onClick={() => handleAcknowledgeEmergency(emergencyDetail.id)}
                              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                            >
                              Reconocer Emergencia
                            </Button>
                          )}
                          <Button
                            onClick={() => handleEscalateEmergency(emergencyDetail.id)}
                            variant="outline"
                            className="border-purple-300 text-purple-600 hover:bg-purple-50"
                          >
                            Escalar a Autoridades
                          </Button>
                        </div>

                        {/* Resolve Section */}
                        <div className="p-4 bg-green-50 rounded-xl">
                          <p className="text-green-700 text-sm font-medium mb-2">Resolver Emergencia</p>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-slate-700 text-sm">Tipo de Resolución</Label>
                              <select
                                value={resolutionType}
                                onChange={(e) => setResolutionType(e.target.value)}
                                className="w-full mt-1 h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm"
                              >
                                <option value="resolved">Resuelta - Situación controlada</option>
                                <option value="false_alarm">Falsa alarma</option>
                                <option value="escalated_external">Escalada a autoridades externas</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-slate-700 text-sm">Notas de Resolución</Label>
                              <Input
                                value={resolutionNotes}
                                onChange={(e) => setResolutionNotes(e.target.value)}
                                placeholder="Describir cómo se resolvió..."
                                className="mt-1"
                              />
                            </div>
                            <Button
                              onClick={() => handleResolveEmergency(emergencyDetail.id)}
                              disabled={!resolutionNotes.trim()}
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Marcar como Resuelta
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Resolution Info (if resolved) */}
                    {emergencyDetail.status === 'resolved' && emergencyDetail.resolution && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-green-700 text-sm font-medium mb-2">✅ Emergencia Resuelta</p>
                        <p className="text-slate-800">{emergencyDetail.resolution.notes}</p>
                        <p className="text-slate-500 text-xs mt-2">
                          Por: {emergencyDetail.resolution.resolved_by_name} • 
                          Tipo: {emergencyDetail.resolution.type} • 
                          Tiempo total: {formatElapsedTime(emergencyDetail.response_time_seconds)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </Card>
          </div>
        )}

        {/* Country Modal */}
        {showCountryModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="card-elevated w-full max-w-md animate-slide-up">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800">{editingCountry ? 'Editar País' : 'Nuevo País'}</h3>
                  <Button variant="ghost" size="icon" onClick={() => setShowCountryModal(false)}>
                    <X className="w-5 h-5 text-slate-400" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Código ISO</Label>
                      <Input
                        value={countryForm.code}
                        onChange={(e) => setCountryForm({...countryForm, code: e.target.value.toUpperCase()})}
                        placeholder="MX"
                        maxLength={2}
                        disabled={!!editingCountry}
                        className="h-10 bg-slate-50 border-slate-200 text-slate-800"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Idioma</Label>
                      <Input
                        value={countryForm.language}
                        onChange={(e) => setCountryForm({...countryForm, language: e.target.value})}
                        placeholder="es"
                        className="h-10 bg-slate-50 border-slate-200 text-slate-800"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-700">Nombre del país</Label>
                    <Input
                      value={countryForm.name}
                      onChange={(e) => setCountryForm({...countryForm, name: e.target.value})}
                      placeholder="México"
                      className="h-10 bg-slate-50 border-slate-200 text-slate-800"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-700">Moneda</Label>
                      <Input
                        value={countryForm.currency}
                        onChange={(e) => setCountryForm({...countryForm, currency: e.target.value.toUpperCase()})}
                        placeholder="MXN"
                        className="h-10 bg-slate-50 border-slate-200 text-slate-800"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Símbolo</Label>
                      <Input
                        value={countryForm.symbol}
                        onChange={(e) => setCountryForm({...countryForm, symbol: e.target.value})}
                        placeholder="$"
                        className="h-10 bg-slate-50 border-slate-200 text-slate-800"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Tasa USD</Label>
                      <Input
                        type="number"
                        value={countryForm.exchange_rate}
                        onChange={(e) => setCountryForm({...countryForm, exchange_rate: parseFloat(e.target.value)})}
                        className="h-10 bg-slate-50 border-slate-200 text-slate-800"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-700">País activo</span>
                    <Switch
                      checked={countryForm.active}
                      onCheckedChange={(checked) => setCountryForm({...countryForm, active: checked})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700">Servicios disponibles</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-slate-600">
                        <input
                          type="checkbox"
                          checked={countryForm.services.rides}
                          onChange={(e) => setCountryForm({...countryForm, services: {...countryForm.services, rides: e.target.checked}})}
                          className="rounded"
                        />
                        Viajes
                      </label>
                      <label className="flex items-center gap-2 text-slate-600">
                        <input
                          type="checkbox"
                          checked={countryForm.services.rentals}
                          onChange={(e) => setCountryForm({...countryForm, services: {...countryForm.services, rentals: e.target.checked}})}
                          className="rounded"
                        />
                        Rentas
                      </label>
                      <label className="flex items-center gap-2 text-slate-600">
                        <input
                          type="checkbox"
                          checked={countryForm.services.deliveries}
                          onChange={(e) => setCountryForm({...countryForm, services: {...countryForm.services, deliveries: e.target.checked}})}
                          className="rounded"
                        />
                        Envíos
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" className="flex-1 border-slate-200 text-slate-600" onClick={() => setShowCountryModal(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveCountry} className="flex-1 btn-primary">
                    Guardar
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
